import React, { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { get, isEmpty } from "lodash";
import {
  getBatchReports,
  reviseReport,
  reviseTimeSeriesData,
} from "services/gaService";
import { useReportContext } from "components/ReportContext/ReportContextProvider";
import { useFTUTooltipsWizardContext } from "./FTUTooltipsWizardContextProvider";
import FTUOverview from "./components/FTUOverview";
import { DIMENSION_DATE } from "constants/dimensions";
import { METRIC_EVENT_COUNT } from "constants/metrics";
import { alertUnknownError } from "services/notificationService";

const DATAKEY_TOOLTIP_OPEN = "tooltipOpen";
const DATAKEY_MERKEN_CLICKS = "merkenClicks";
const DATAKEY_CLICK_RATE = "clickRate";

const FTUTooltipsWizard = () => {
  const { reportRequests } = useFTUTooltipsWizardContext();
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { dateStrings } = useReportContext();

  const resetData = () => {
    setReports([]);
  };

  useEffect(() => {
    if (!reportRequests) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const data = await getBatchReports(reportRequests);
        setReports(get(data, "reports", []));
      } catch (error) {
        alertUnknownError();
        resetData();
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [reportRequests]);

  const tooltipOpenReport = useMemo(() => {
    return reviseReport(reports[0]);
  }, [reports]);

  const merkenClicksReport = useMemo(() => {
    return reviseReport(reports[1]);
  }, [reports]);

  const timeSeriesData = useMemo(() => {
    if (isEmpty(tooltipOpenReport) && isEmpty(merkenClicksReport)) return [];

    // group tooltip open by date (report 0: EVENT_OPEN_ITEM, CATEGORY_TOOLTIP_V1)
    const tooltipOpenByDate = (tooltipOpenReport.rows || []).reduce(
      (result, current) => {
        const date = current[DIMENSION_DATE];
        result[date] = current[METRIC_EVENT_COUNT] || 0;
        return result;
      },
      {}
    );

    // group merken clicks by date
    const merkenClicksByDate = (merkenClicksReport.rows || []).reduce(
      (result, current) => {
        const date = current[DIMENSION_DATE];
        result[date] = current[METRIC_EVENT_COUNT] || 0;
        return result;
      },
      {}
    );

    // combine both datasets
    const allDates = [
      ...Object.keys(tooltipOpenByDate),
      ...Object.keys(merkenClicksByDate),
    ];
    const uniqueDates = [...new Set(allDates)];

    const combinedData = uniqueDates.map((date) => {
      const tooltipOpen = tooltipOpenByDate[date] || 0;
      const merkenClicks = merkenClicksByDate[date] || 0;
      const clickRate =
        tooltipOpen > 0
          ? parseFloat(((merkenClicks / tooltipOpen) * 100).toFixed(2))
          : 0;

      return {
        [DIMENSION_DATE]: date,
        [DATAKEY_TOOLTIP_OPEN]: tooltipOpen,
        [DATAKEY_MERKEN_CLICKS]: merkenClicks,
        [DATAKEY_CLICK_RATE]: clickRate,
      };
    });

    // fill in missing dates with zeros
    const series = reviseTimeSeriesData(combinedData, {
      dateKey: DIMENSION_DATE,
      startDate: dateStrings[0],
      endDate: dateStrings[1],
      dataKeys: [
        DATAKEY_TOOLTIP_OPEN,
        DATAKEY_MERKEN_CLICKS,
        DATAKEY_CLICK_RATE,
      ],
    });

    // recalculate click rate for filled dates
    return series.map((item) => {
      const tooltipOpen = item[DATAKEY_TOOLTIP_OPEN] || 0;
      const merkenClicks = item[DATAKEY_MERKEN_CLICKS] || 0;
      const clickRate =
        tooltipOpen > 0
          ? parseFloat(((merkenClicks / tooltipOpen) * 100).toFixed(2))
          : 0;

      return {
        ...item,
        [DATAKEY_CLICK_RATE]: clickRate,
      };
    });
  }, [tooltipOpenReport, merkenClicksReport, dateStrings]);

  return (
    <Spin spinning={isLoading}>
      <FTUOverview data={timeSeriesData} />
    </Spin>
  );
};

export default FTUTooltipsWizard;
